import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Download,
  FilePlus2,
  FileText,
  History,
  ListChecks,
  Mail,
  Printer,
  RotateCcw,
  Save,
  Settings2,
  Tags,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const inr = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const METHOD_LABELS = {
  FIXED: "Fixed",
  PER_SQFT: "Per sq. ft.",
  PERCENTAGE: "Percentage",
  VARIABLE: "Variable",
};

const STATUS_STYLES = {
  DRAFT: "bg-amber-50 text-amber-800 border-amber-200",
  FINALIZED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  REVERSED: "bg-rose-50 text-rose-800 border-rose-200",
};

const emptyCharge = {
  code: "",
  name: "",
  calculation_method: "FIXED",
  rate: "",
  area_basis: "CARPET",
  base_charge_id: "",
  income_account_id: "",
  display_order: 100,
  attracts_late_fee: true,
};

const emptyPeriod = {
  label: "",
  start_date: "",
  end_date: "",
  due_date: "",
  late_fee_annual_percent: "12",
};

const BILLING_TABS = [
  ["charges", "Charges Master", Tags],
  ["assign", "Assign Charges", ListChecks],
  ["opening", "Old Data (Opening Dues)", History],
  ["generate", "Generate Bills", FilePlus2],
  ["register", "Bill Register", FileText],
  ["settings", "Bill Settings", Settings2],
];

export default function FreshFinanceBilling({ accounts = [], access }) {
  const permissions = access?.permissions || [];
  const can = (name) => permissions.includes(name);
  const [tab, setTab] = useState("charges");
  const [charges, setCharges] = useState([]);
  const [units, setUnits] = useState([]);
  const [periods, setPeriods] = useState([]);

  const reload = async () => {
    try {
      const [chargeRes, unitRes, periodRes] = await Promise.all([
        api.get("/fresh-finance/charges"),
        api.get("/fresh-finance/masters/units", { params: { limit: 5000 } }),
        api.get("/fresh-finance/bill-periods"),
      ]);
      setCharges(chargeRes.data || []);
      setUnits(unitRes.data.items || []);
      setPeriods(periodRes.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load billing data",
      );
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const incomeAccounts = accounts.filter(
    (item) => item.account_type === "INCOME",
  );

  return (
    <section data-testid="fresh-finance-billing">
      <div className="border-b border-gray-200 pb-3">
        <h2 className="mt-1 font-display text-2xl font-bold">
          Bills, Charges &amp; Payments
        </h2>
      </div>
      <div
        className="mt-4 flex flex-wrap border-b border-gray-200"
        data-testid="fresh-finance-billing-tabs"
      >
        {BILLING_TABS.map(([id, label, Icon]) => (
          <button
            className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium ${
              tab === id
                ? "border-blue-700 text-blue-800"
                : "border-transparent text-gray-600 hover:text-gray-950"
            }`}
            data-testid={`fresh-finance-billing-tab-${id}`}
            key={id}
            onClick={() => setTab(id)}
            type="button"
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "charges" && (
        <ChargesTab
          accounts={incomeAccounts}
          canManage={can("manage_charges")}
          charges={charges}
          onReload={reload}
        />
      )}
      {tab === "assign" && (
        <AssignTab
          canManage={can("manage_assignments")}
          charges={charges}
          units={units}
        />
      )}
      {tab === "opening" && (
        <OpeningDuesTab
          canManage={can("manage_opening_balances")}
          charges={charges}
          units={units}
        />
      )}
      {tab === "generate" && (
        <GenerateTab
          canManage={can("manage_billing")}
          canFinalize={can("finalize_bills")}
          charges={charges}
          periods={periods}
          units={units}
          onReload={reload}
        />
      )}
      {tab === "register" && (
        <RegisterTab canFinalize={can("finalize_bills")} periods={periods} />
      )}
      {tab === "settings" && (
        <SettingsTab canManage={can("manage_billing")} />
      )}
    </section>
  );
}

function ChargesTab({ accounts, canManage, charges, onReload }) {
  const [form, setForm] = useState(emptyCharge);
  const percentageBases = charges.filter(
    (item) => item.calculation_method !== "PERCENTAGE",
  );
  const method = form.calculation_method;

  const save = async () => {
    try {
      await api.post("/fresh-finance/charges", {
        ...form,
        rate: Number(form.rate || 0),
        display_order: Number(form.display_order || 100),
        area_basis: method === "PER_SQFT" ? form.area_basis : "CARPET",
        base_charge_id: method === "PERCENTAGE" ? form.base_charge_id : "",
      });
      toast.success("Charge added");
      setForm(emptyCharge);
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save charge");
    }
  };

  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-charges">
      <section
        className="border border-gray-200 bg-white p-4"
        data-testid="fresh-finance-charge-form"
      >
        <h3 className="font-display text-sm font-bold">Add charge head</h3>
        <fieldset
          className="mt-3 flex flex-wrap items-end gap-3 disabled:opacity-50"
          disabled={!canManage}
        >
          <Field label="Code">
            <Input
              className="h-8 w-28 border-gray-300 text-sm"
              data-testid="fresh-finance-charge-code"
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              value={form.code}
            />
          </Field>
          <Field label="Charge name">
            <Input
              className="h-8 w-52 border-gray-300 text-sm"
              data-testid="fresh-finance-charge-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              value={form.name}
            />
          </Field>
          <Field label="Method">
            <select
              className="h-8 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-charge-method"
              onChange={(e) =>
                setForm({ ...form, calculation_method: e.target.value })
              }
              value={method}
            >
              {Object.entries(METHOD_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={method === "PERCENTAGE" ? "Rate %" : "Rate (₹)"}
          >
            <Input
              className="h-8 w-28 border-gray-300 text-sm"
              data-testid="fresh-finance-charge-rate"
              disabled={method === "VARIABLE"}
              onChange={(e) => setForm({ ...form, rate: e.target.value })}
              type="number"
              value={form.rate}
            />
          </Field>
          {method === "PER_SQFT" && (
            <Field label="Area basis">
              <select
                className="h-8 border border-gray-300 bg-white px-2 text-sm"
                data-testid="fresh-finance-charge-area-basis"
                onChange={(e) =>
                  setForm({ ...form, area_basis: e.target.value })
                }
                value={form.area_basis}
              >
                <option value="CARPET">Carpet</option>
                <option value="BUILT_UP">Built-up</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </Field>
          )}
          {method === "PERCENTAGE" && (
            <Field label="Base charge">
              <select
                className="h-8 border border-gray-300 bg-white px-2 text-sm"
                data-testid="fresh-finance-charge-base"
                onChange={(e) =>
                  setForm({ ...form, base_charge_id: e.target.value })
                }
                value={form.base_charge_id}
              >
                <option value="">Select base</option>
                {percentageBases.map((item) => (
                  <option key={item.charge_id} value={item.charge_id}>
                    {item.code} · {item.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Income account">
            <select
              className="h-8 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-charge-account"
              onChange={(e) =>
                setForm({ ...form, income_account_id: e.target.value })
              }
              value={form.income_account_id}
            >
              <option value="">Default (Maintenance Income)</option>
              {accounts.map((item) => (
                <option key={item.account_id} value={item.account_id}>
                  {item.code} · {item.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Order">
            <Input
              className="h-8 w-20 border-gray-300 text-sm"
              data-testid="fresh-finance-charge-order"
              onChange={(e) =>
                setForm({ ...form, display_order: e.target.value })
              }
              type="number"
              value={form.display_order}
            />
          </Field>
          <label className="flex h-8 items-center gap-2 text-xs font-semibold text-gray-700">
            <input
              checked={form.attracts_late_fee}
              data-testid="fresh-finance-charge-late-fee"
              onChange={(e) =>
                setForm({ ...form, attracts_late_fee: e.target.checked })
              }
              type="checkbox"
            />
            Maintenance (late fee applies)
          </label>
          <Button
            data-testid="fresh-finance-charge-save"
            onClick={save}
            size="sm"
            type="button"
          >
            <Save className="h-4 w-4" />
            Save
          </Button>
        </fieldset>
      </section>

      <div
        className="overflow-x-auto border border-gray-200"
        data-testid="fresh-finance-charges-table"
      >
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Method</th>
              <th className="px-3 py-2 text-right">Rate</th>
              <th className="px-3 py-2">Basis / Base</th>
              <th className="px-3 py-2">Late fee</th>
              <th className="px-3 py-2 text-right">Order</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {charges.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-gray-500" colSpan={7}>
                  No charge heads yet.
                </td>
              </tr>
            )}
            {charges.map((item) => (
              <tr key={item.charge_id} data-testid={`fresh-finance-charge-row-${item.code}`}>
                <td className="px-3 py-2 font-medium">{item.code}</td>
                <td className="px-3 py-2">{item.name}</td>
                <td className="px-3 py-2">
                  {METHOD_LABELS[item.calculation_method]}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {item.calculation_method === "PERCENTAGE"
                    ? `${Number(item.rate)}%`
                    : item.calculation_method === "VARIABLE"
                      ? "—"
                      : inr(item.rate)}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {item.calculation_method === "PER_SQFT" && item.area_basis}
                  {item.calculation_method === "PERCENTAGE" &&
                    (charges.find((c) => c.charge_id === item.base_charge_id)
                      ?.code || "—")}
                </td>
                <td className="px-3 py-2 text-xs">
                  {item.attracts_late_fee === false ? (
                    <span className="text-gray-500">No</span>
                  ) : (
                    <span className="font-semibold text-emerald-700">Yes</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {item.display_order}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssignTab({ canManage, charges, units }) {
  const [chargeId, setChargeId] = useState("");
  const [scope, setScope] = useState("ALL_UNITS");
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!chargeId) return;
    (async () => {
      try {
        const res = await api.get(
          `/fresh-finance/charges/${chargeId}/assignment`,
        );
        setScope(res.data.scope || "ALL_UNITS");
        setSelected(res.data.unit_ids || []);
      } catch (error) {
        toast.error("Could not load assignment");
      }
    })();
  }, [chargeId]);

  const filteredUnits = units.filter((item) =>
    `${item.unit_number} ${item.building_name || ""} ${item.wing_name || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const save = async () => {
    if (!chargeId) {
      toast.error("Select a charge first");
      return;
    }
    try {
      await api.put(`/fresh-finance/charges/${chargeId}/assignment`, {
        scope,
        unit_ids: scope === "SELECTED_UNITS" ? selected : [],
      });
      toast.success("Assignment saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save assignment");
    }
  };

  return (
    <div
      className="mt-4 grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]"
      data-testid="fresh-finance-assign"
    >
      <div className="border border-gray-200 bg-white p-3">
        <h3 className="font-display text-sm font-bold">Charge heads</h3>
        <div className="mt-2 grid gap-1" data-testid="fresh-finance-assign-charge-list">
          {charges.map((item) => (
            <button
              className={`border px-3 py-2 text-left text-sm ${
                chargeId === item.charge_id
                  ? "border-blue-700 bg-blue-50 text-blue-800"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
              data-testid={`fresh-finance-assign-charge-${item.code}`}
              key={item.charge_id}
              onClick={() => setChargeId(item.charge_id)}
              type="button"
            >
              {item.code} · {item.name}
            </button>
          ))}
          {charges.length === 0 && (
            <p className="text-sm text-gray-500">Add charge heads first.</p>
          )}
        </div>
      </div>

      <div className="border border-gray-200 bg-white p-4">
        {!chargeId ? (
          <p className="text-sm text-gray-500">
            Select a charge head to assign it to units.
          </p>
        ) : (
          <fieldset disabled={!canManage} className="disabled:opacity-60">
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  checked={scope === "ALL_UNITS"}
                  data-testid="fresh-finance-assign-scope-all"
                  onChange={() => setScope("ALL_UNITS")}
                  type="radio"
                />
                All active units
              </label>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  checked={scope === "SELECTED_UNITS"}
                  data-testid="fresh-finance-assign-scope-selected"
                  onChange={() => setScope("SELECTED_UNITS")}
                  type="radio"
                />
                Selected units
              </label>
              <Button
                className="ml-auto"
                data-testid="fresh-finance-assign-save"
                onClick={save}
                size="sm"
                type="button"
              >
                <Save className="h-4 w-4" />
                Save assignment
              </Button>
            </div>
            {scope === "SELECTED_UNITS" && (
              <div className="mt-4">
                <div className="flex items-center gap-3">
                  <Input
                    className="h-8 w-56 border-gray-300 text-sm"
                    data-testid="fresh-finance-assign-search"
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search units"
                    value={search}
                  />
                  <button
                    className="text-xs font-semibold text-blue-700"
                    data-testid="fresh-finance-assign-select-all"
                    onClick={() =>
                      setSelected(filteredUnits.map((u) => u.unit_id))
                    }
                    type="button"
                  >
                    Select all shown
                  </button>
                  <button
                    className="text-xs font-semibold text-gray-600"
                    data-testid="fresh-finance-assign-clear"
                    onClick={() => setSelected([])}
                    type="button"
                  >
                    Clear
                  </button>
                  <span className="text-xs text-gray-500">
                    {selected.length} selected
                  </span>
                </div>
                <div className="mt-3 grid max-h-80 grid-cols-2 gap-1 overflow-auto sm:grid-cols-3 lg:grid-cols-4">
                  {filteredUnits.map((unit) => (
                    <label
                      className="flex items-center gap-2 border border-gray-200 px-2 py-1 text-sm"
                      key={unit.unit_id}
                    >
                      <input
                        checked={selected.includes(unit.unit_id)}
                        data-testid={`fresh-finance-assign-unit-${unit.unit_number}`}
                        onChange={() => toggle(unit.unit_id)}
                        type="checkbox"
                      />
                      {unit.unit_number}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </fieldset>
        )}
      </div>
    </div>
  );
}

function GenerateTab({
  canFinalize,
  canManage,
  charges,
  onReload,
  periods,
  units,
}) {
  const [period, setPeriod] = useState(emptyPeriod);
  const [periodId, setPeriodId] = useState("");
  const [preview, setPreview] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);

  const hasVariable = charges.some(
    (item) => item.calculation_method === "VARIABLE",
  );

  const loadDrafts = async (pid) => {
    if (!pid) {
      setDrafts([]);
      return;
    }
    const res = await api.get("/fresh-finance/bills", {
      params: { period_id: pid },
    });
    setDrafts(res.data || []);
  };

  useEffect(() => {
    loadDrafts(periodId);
    setPreview(null);
  }, [periodId]);

  const createPeriod = async () => {
    try {
      const res = await api.post("/fresh-finance/bill-periods", {
        ...period,
        late_fee_annual_percent: Number(period.late_fee_annual_percent || 0),
      });
      toast.success("Bill period created");
      setPeriod(emptyPeriod);
      onReload();
      setPeriodId(res.data.period_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create period");
    }
  };

  const runPreview = async () => {
    try {
      setBusy(true);
      const res = await api.post(
        `/fresh-finance/bill-periods/${periodId}/preview`,
        { unit_ids: [] },
      );
      setPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    try {
      setBusy(true);
      const res = await api.post(
        `/fresh-finance/bill-periods/${periodId}/generate-drafts`,
        { unit_ids: [] },
      );
      toast.success(`${res.data.created} draft bill(s) generated`);
      loadDrafts(periodId);
      setPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const finalizeOne = async (billId) => {
    try {
      await api.post(`/fresh-finance/bills/${billId}/finalize`);
      toast.success("Bill finalized");
      loadDrafts(periodId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Finalize failed");
    }
  };

  const finalizeAll = async () => {
    const draftBills = drafts.filter((b) => b.status === "DRAFT");
    for (const bill of draftBills) {
      try {
        await api.post(`/fresh-finance/bills/${bill.bill_id}/finalize`);
      } catch (error) {
        toast.error(`Failed: ${bill.unit_snapshot?.unit_number}`);
      }
    }
    toast.success("Finalization complete");
    loadDrafts(periodId);
  };

  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-generate">
      <section
        className="border border-gray-200 bg-white p-4"
        data-testid="fresh-finance-period-form"
      >
        <h3 className="font-display text-sm font-bold">Create bill period</h3>
        <fieldset
          className="mt-3 flex flex-wrap items-end gap-3 disabled:opacity-50"
          disabled={!canManage}
        >
          <Field label="Label (e.g. Apr 2026)">
            <Input
              className="h-8 w-40 border-gray-300 text-sm"
              data-testid="fresh-finance-period-label"
              onChange={(e) => setPeriod({ ...period, label: e.target.value })}
              value={period.label}
            />
          </Field>
          <Field label="Start">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-period-start"
              onChange={(e) =>
                setPeriod({ ...period, start_date: e.target.value })
              }
              type="date"
              value={period.start_date}
            />
          </Field>
          <Field label="End">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-period-end"
              onChange={(e) =>
                setPeriod({ ...period, end_date: e.target.value })
              }
              type="date"
              value={period.end_date}
            />
          </Field>
          <Field label="Due">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-period-due"
              onChange={(e) =>
                setPeriod({ ...period, due_date: e.target.value })
              }
              type="date"
              value={period.due_date}
            />
          </Field>
          <Field label="Late fee % p.a. (÷12 monthly on maintenance arrears)">
            <Input
              className="h-8 w-24 border-gray-300 text-sm"
              data-testid="fresh-finance-period-interest"
              onChange={(e) =>
                setPeriod({ ...period, late_fee_annual_percent: e.target.value })
              }
              type="number"
              value={period.late_fee_annual_percent}
            />
          </Field>
          <Button
            data-testid="fresh-finance-period-save"
            onClick={createPeriod}
            size="sm"
            type="button"
          >
            <Save className="h-4 w-4" />
            Create
          </Button>
        </fieldset>
      </section>

      <section className="border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Bill period">
            <select
              className="h-8 min-w-48 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-generate-period"
              onChange={(e) => setPeriodId(e.target.value)}
              value={periodId}
            >
              <option value="">Select period</option>
              {periods.map((item) => (
                <option key={item.period_id} value={item.period_id}>
                  {item.label} ({item.status})
                </option>
              ))}
            </select>
          </Field>
          <Button
            data-testid="fresh-finance-preview-btn"
            disabled={!periodId || busy}
            onClick={runPreview}
            size="sm"
            type="button"
            variant="outline"
          >
            Preview
          </Button>
          <Button
            data-testid="fresh-finance-generate-btn"
            disabled={!periodId || busy || !canManage}
            onClick={generate}
            size="sm"
            type="button"
          >
            <FilePlus2 className="h-4 w-4" />
            Generate drafts
          </Button>
        </div>

        {hasVariable && periodId && (
          <VariableValues
            charges={charges.filter(
              (c) => c.calculation_method === "VARIABLE",
            )}
            canManage={canManage}
            periodId={periodId}
            units={units}
          />
        )}

        {preview && (
          <div
            className="mt-4 grid gap-px border border-gray-200 bg-gray-200 sm:grid-cols-4"
            data-testid="fresh-finance-preview-counts"
          >
            {[
              ["Ready", preview.counts.generated],
              ["Already billed", preview.counts.already_billed],
              ["Skipped", preview.counts.skipped],
              ["Failed", preview.counts.failed],
            ].map(([label, value]) => (
              <div className="bg-white p-3" key={label}>
                <p className="text-xs uppercase text-gray-600">{label}</p>
                <p className="mt-1 font-display text-2xl font-bold tabular-nums">
                  {value}
                </p>
              </div>
            ))}
          </div>
        )}
        {preview?.items?.some((i) => i.state === "FAILED") && (
          <div className="mt-3 border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
            {preview.items
              .filter((i) => i.state === "FAILED")
              .map((i) => (
                <p key={i.unit_id}>
                  {i.unit_number}: {(i.errors || []).join(", ")}
                </p>
              ))}
          </div>
        )}
      </section>

      {drafts.length > 0 && (
        <DraftList
          bills={drafts}
          canFinalize={canFinalize}
          onFinalizeAll={finalizeAll}
          onFinalizeOne={finalizeOne}
          onReload={() => loadDrafts(periodId)}
          canManage={canManage}
        />
      )}
    </div>
  );
}

function VariableValues({ canManage, charges, periodId, units }) {
  const [chargeId, setChargeId] = useState(charges[0]?.charge_id || "");
  const [values, setValues] = useState({});

  const saveValue = async (unitId, amount) => {
    try {
      await api.put(
        `/fresh-finance/bill-periods/${periodId}/variables/${chargeId}/${unitId}`,
        { amount: Number(amount || 0) },
      );
      toast.success("Saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save value");
    }
  };

  return (
    <div
      className="mt-4 border border-gray-200 p-3"
      data-testid="fresh-finance-variable-values"
    >
      <div className="flex items-center gap-3">
        <h4 className="text-sm font-bold">Variable charge values</h4>
        <select
          className="h-8 border border-gray-300 bg-white px-2 text-sm"
          data-testid="fresh-finance-variable-charge"
          onChange={(e) => setChargeId(e.target.value)}
          value={chargeId}
        >
          {charges.map((item) => (
            <option key={item.charge_id} value={item.charge_id}>
              {item.code} · {item.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-3 grid max-h-60 grid-cols-2 gap-2 overflow-auto sm:grid-cols-3 lg:grid-cols-4">
        {units.map((unit) => (
          <div className="flex items-center gap-1" key={unit.unit_id}>
            <span className="w-16 text-xs text-gray-600">
              {unit.unit_number}
            </span>
            <Input
              className="h-7 border-gray-300 text-sm"
              data-testid={`fresh-finance-variable-input-${unit.unit_number}`}
              disabled={!canManage}
              onBlur={(e) => saveValue(unit.unit_id, e.target.value)}
              onChange={(e) =>
                setValues({ ...values, [unit.unit_id]: e.target.value })
              }
              type="number"
              value={values[unit.unit_id] ?? ""}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function DraftList({
  bills,
  canFinalize,
  canManage,
  onFinalizeAll,
  onFinalizeOne,
  onReload,
}) {
  const [editing, setEditing] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const drafts = bills.filter((b) => b.status === "DRAFT");
  const draftTotal = drafts.reduce((sum, b) => sum + Number(b.grand_total || 0), 0);

  return (
    <section
      className="border border-gray-200 bg-white p-4"
      data-testid="fresh-finance-drafts"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-bold">
          Draft bills ({drafts.length})
        </h3>
        {drafts.length > 0 && canFinalize && (
          <Button
            data-testid="fresh-finance-finalize-all"
            onClick={() => setConfirmAll(true)}
            size="sm"
            type="button"
          >
            <CheckCircle2 className="h-4 w-4" />
            Finalize all
          </Button>
        )}
      </div>
      <div className="mt-3 overflow-x-auto border border-gray-200">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2">Member</th>
              <th className="px-3 py-2 text-right">Maintenance</th>
              <th className="px-3 py-2 text-right">Other charges</th>
              <th className="px-3 py-2 text-right">Arrears</th>
              <th className="px-3 py-2 text-right">Late fee</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {bills.map((bill) => (
              <tr
                key={bill.bill_id}
                data-testid={`fresh-finance-draft-row-${bill.unit_snapshot?.unit_number}`}
              >
                <td className="px-3 py-2 font-medium">
                  {bill.unit_snapshot?.unit_number}
                </td>
                <td className="px-3 py-2">
                  {bill.member_snapshot?.name || "—"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {inr(bill.maintenance_total ?? bill.new_charge_total)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {inr(bill.other_charge_total ?? 0)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {inr(bill.previous_due)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {inr(bill.interest_amount)}
                </td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {inr(bill.grand_total)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[bill.status]}`}
                  >
                    {bill.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {bill.status === "DRAFT" && (
                    <div className="flex justify-end gap-2">
                      {canManage && (
                        <button
                          className="text-xs font-semibold text-blue-700"
                          data-testid={`fresh-finance-draft-edit-${bill.unit_snapshot?.unit_number}`}
                          onClick={() => setEditing(bill)}
                          type="button"
                        >
                          Edit
                        </button>
                      )}
                      {canFinalize && (
                        <button
                          className="text-xs font-semibold text-emerald-700"
                          data-testid={`fresh-finance-draft-finalize-${bill.unit_snapshot?.unit_number}`}
                          onClick={() => onFinalizeOne(bill.bill_id)}
                          type="button"
                        >
                          Finalize
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <EditDraftModal
          bill={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onReload();
          }}
        />
      )}
      {confirmAll && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="fresh-finance-finalize-all-modal"
        >
          <div className="w-full max-w-md border border-gray-200 bg-white p-5">
            <h3 className="font-display text-lg font-bold">Finalize all drafts?</h3>
            <p className="mt-2 text-sm text-gray-600">
              You are about to finalize <b>{drafts.length}</b> draft bill(s)
              totalling <b>{inr(draftTotal)}</b>. Finalized bills get a bill
              number, post balanced journal entries and become immutable (they can
              only be corrected via reversal).
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                data-testid="fresh-finance-finalize-all-cancel"
                onClick={() => setConfirmAll(false)}
                size="sm"
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                data-testid="fresh-finance-finalize-all-confirm"
                onClick={() => {
                  setConfirmAll(false);
                  onFinalizeAll();
                }}
                size="sm"
                type="button"
              >
                <CheckCircle2 className="h-4 w-4" />
                Confirm finalize {drafts.length}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EditDraftModal({ bill, onClose, onSaved }) {
  const [lines, setLines] = useState(bill.lines.map((l) => ({ ...l })));
  const [notes, setNotes] = useState(bill.notes || "");

  const total = lines.reduce((sum, l) => sum + Number(l.amount || 0), 0);

  const save = async () => {
    try {
      await api.patch(`/fresh-finance/bills/${bill.bill_id}`, {
        notes,
        lines: lines.map((l) => ({ ...l, amount: String(Number(l.amount || 0)) })),
      });
      toast.success("Draft updated");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update draft");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="fresh-finance-edit-draft-modal"
    >
      <div className="w-full max-w-lg border border-gray-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold">
          Edit draft · {bill.unit_snapshot?.unit_number}
        </h3>
        <div className="mt-3 space-y-2">
          {lines.map((line, index) => (
            <div className="flex items-center gap-2" key={line.charge_id || index}>
              <span className="flex-1 text-sm">{line.name}</span>
              <Input
                className="h-8 w-32 border-gray-300 text-right text-sm"
                data-testid={`fresh-finance-edit-line-${index}`}
                onChange={(e) => {
                  const next = [...lines];
                  next[index] = { ...next[index], amount: e.target.value };
                  setLines(next);
                }}
                type="number"
                value={line.amount}
              />
            </div>
          ))}
        </div>
        <div className="mt-3">
          <Label className="text-xs">Notes</Label>
          <Input
            className="mt-1 h-8 border-gray-300 text-sm"
            data-testid="fresh-finance-edit-notes"
            onChange={(e) => setNotes(e.target.value)}
            value={notes}
          />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-semibold">
            Current charges: {inr(total)}
          </span>
          <div className="flex gap-2">
            <Button
              data-testid="fresh-finance-edit-cancel"
              onClick={onClose}
              size="sm"
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              data-testid="fresh-finance-edit-save"
              onClick={save}
              size="sm"
              type="button"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegisterTab({ canFinalize, periods }) {
  const [periodId, setPeriodId] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [bills, setBills] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reversing, setReversing] = useState(null);

  const load = async () => {
    const res = await api.get("/fresh-finance/bills", {
      params: periodId ? { period_id: periodId } : {},
    });
    setBills(res.data || []);
    setSelected([]);
  };

  useEffect(() => {
    load();
  }, [periodId]);

  const filtered = bills.filter((bill) => {
    if (status && bill.status !== status) return false;
    if (
      search &&
      !`${bill.unit_snapshot?.unit_number} ${bill.bill_number || ""} ${bill.member_snapshot?.name || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    )
      return false;
    return true;
  });

  const openPdf = async (billId) => {
    try {
      const res = await api.get(`/fresh-finance/bills/${billId}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (error) {
      toast.error("Could not open PDF");
    }
  };

  const printSelected = async () => {
    for (const id of selected) {
      await openPdf(id);
    }
  };

  const emailBill = async (billId) => {
    try {
      const response = await api.post(`/fresh-finance/bills/${billId}/email`);
      toast.success(
        response.data.status === "ALREADY_SENT"
          ? "This bill was already emailed"
          : "Bill emailed to the member",
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not email the bill");
    }
  };

  const emailSelected = async () => {
    const billIds = filtered
      .filter((bill) => selected.includes(bill.bill_id) && bill.status === "FINALIZED")
      .map((bill) => bill.bill_id);
    if (!billIds.length) {
      toast.error("Select at least one finalized bill to email");
      return;
    }
    try {
      const response = await api.post("/fresh-finance/bills/email/batch", { bill_ids: billIds });
      const failed = response.data.results.filter((item) => item.status === "FAILED").length;
      toast.success(
        failed ? `${billIds.length - failed} bills emailed; ${failed} need attention` : "Bills emailed",
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not email selected bills");
    }
  };

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  return (
    <div className="mt-4 space-y-4" data-testid="fresh-finance-register">
      <section className="flex flex-wrap items-end gap-3 border border-gray-200 bg-white p-4">
        <Field label="Period">
          <select
            className="h-8 min-w-44 border border-gray-300 bg-white px-2 text-sm"
            data-testid="fresh-finance-register-period"
            onChange={(e) => setPeriodId(e.target.value)}
            value={periodId}
          >
            <option value="">All periods</option>
            {periods.map((item) => (
              <option key={item.period_id} value={item.period_id}>
                {item.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select
            className="h-8 border border-gray-300 bg-white px-2 text-sm"
            data-testid="fresh-finance-register-status"
            onChange={(e) => setStatus(e.target.value)}
            value={status}
          >
            <option value="">All</option>
            <option value="DRAFT">Draft</option>
            <option value="FINALIZED">Finalized</option>
            <option value="REVERSED">Reversed</option>
          </select>
        </Field>
        <Field label="Search">
          <Input
            className="h-8 w-52 border-gray-300 text-sm"
            data-testid="fresh-finance-register-search"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Unit, bill no. or member"
            value={search}
          />
        </Field>
        <Button
          className="ml-auto"
          data-testid="fresh-finance-register-print-selected"
          disabled={selected.length === 0}
          onClick={printSelected}
          size="sm"
          type="button"
          variant="outline"
        >
          <Printer className="h-4 w-4" />
          Print selected ({selected.length})
        </Button>
        <Button
          data-testid="fresh-finance-register-email-selected"
          disabled={selected.length === 0 || !canFinalize}
          onClick={emailSelected}
          size="sm"
          type="button"
        >
          <Mail className="h-4 w-4" />
          Email selected
        </Button>
      </section>

      <div
        className="overflow-x-auto border border-gray-200"
        data-testid="fresh-finance-register-table"
      >
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2"></th>
              <th className="px-3 py-2">Bill no.</th>
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2">Member</th>
              <th className="px-3 py-2">Period</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filtered.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-gray-500" colSpan={8}>
                  No bills found.
                </td>
              </tr>
            )}
            {filtered.map((bill) => (
              <tr
                key={bill.bill_id}
                data-testid={`fresh-finance-register-row-${bill.unit_snapshot?.unit_number}`}
              >
                <td className="px-3 py-2">
                  <input
                    checked={selected.includes(bill.bill_id)}
                    data-testid={`fresh-finance-register-check-${bill.unit_snapshot?.unit_number}`}
                    onChange={() => toggle(bill.bill_id)}
                    type="checkbox"
                  />
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {bill.bill_number || "—"}
                </td>
                <td className="px-3 py-2 font-medium">
                  {bill.unit_snapshot?.unit_number}
                </td>
                <td className="px-3 py-2">
                  {bill.member_snapshot?.name || "—"}
                </td>
                <td className="px-3 py-2">{bill.period_snapshot?.label}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {inr(bill.grand_total)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[bill.status]}`}
                  >
                    {bill.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      className="text-xs font-semibold text-blue-700"
                      data-testid={`fresh-finance-register-pdf-${bill.unit_snapshot?.unit_number}`}
                      onClick={() => openPdf(bill.bill_id)}
                      type="button"
                    >
                      PDF
                    </button>
                    {bill.status === "FINALIZED" && canFinalize && (
                      <button
                        className="text-xs font-semibold text-emerald-700"
                        data-testid={`fresh-finance-register-email-${bill.unit_snapshot?.unit_number}`}
                        onClick={() => emailBill(bill.bill_id)}
                        type="button"
                      >
                        <Mail className="mr-1 inline h-3 w-3" />
                        Email
                      </button>
                    )}
                    {bill.status === "FINALIZED" && canFinalize && (
                      <button
                        className="text-xs font-semibold text-rose-700"
                        data-testid={`fresh-finance-register-reverse-${bill.unit_snapshot?.unit_number}`}
                        onClick={() => setReversing(bill)}
                        type="button"
                      >
                        <RotateCcw className="mr-1 inline h-3 w-3" />
                        Reverse
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {reversing && (
        <ReverseModal
          bill={reversing}
          onClose={() => setReversing(null)}
          onDone={() => {
            setReversing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ReverseModal({ bill, onClose, onDone }) {
  const [reason, setReason] = useState("");

  const reverse = async () => {
    if (!reason.trim()) {
      toast.error("Reversal reason is required");
      return;
    }
    try {
      await api.post(`/fresh-finance/bills/${bill.bill_id}/reverse`, { reason });
      toast.success("Bill reversed with a balancing journal entry");
      onDone();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reverse bill");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="fresh-finance-reverse-modal"
    >
      <div className="w-full max-w-md border border-gray-200 bg-white p-5">
        <h3 className="font-display text-lg font-bold">Reverse bill</h3>
        <p className="mt-1 text-sm text-gray-600">
          {bill.bill_number} · {bill.unit_snapshot?.unit_number} ·{" "}
          {inr(bill.grand_total)}
        </p>
        <p className="mt-2 text-xs text-gray-500">
          The finalized bill stays immutable. A balancing reversal journal entry
          will be posted and the bill marked REVERSED.
        </p>
        <Label className="mt-3 block text-xs">Reason</Label>
        <Input
          className="mt-1 h-8 border-gray-300 text-sm"
          data-testid="fresh-finance-reverse-reason"
          onChange={(e) => setReason(e.target.value)}
          value={reason}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button
            data-testid="fresh-finance-reverse-cancel"
            onClick={onClose}
            size="sm"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="fresh-finance-reverse-confirm"
            onClick={reverse}
            size="sm"
            type="button"
          >
            Confirm reversal
          </Button>
        </div>
      </div>
    </div>
  );
}

function SettingsTab({ canManage }) {
  const [profile, setProfile] = useState({
    bank_name: "",
    account_name: "",
    account_number: "",
    ifsc: "",
    branch: "",
    upi_id: "",
    payment_payee_name: "",
    payment_provider: "UPI",
    payment_qr_enabled: false,
    delivery_email: "",
    delivery_mobile: "",
    payment_instructions: "",
  });
  const [hasLogo, setHasLogo] = useState(false);
  const [seriesData, setSeriesData] = useState({ years: [], series: {} });
  const [seriesYearId, setSeriesYearId] = useState("");
  const [seriesForm, setSeriesForm] = useState({
    bill_prefix: "FF-",
    bill_next_number: 1,
    receipt_prefix: "RCPT-",
    receipt_next_number: 1,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/fresh-finance/settings/billing-profile");
        const { has_logo, ...rest } = res.data || {};
        setProfile((prev) => ({
          ...prev,
          ...rest,
          payment_provider:
            rest.payment_provider === "RAZORPAY_PENDING"
              ? "RAZORPAY"
              : rest.payment_provider || prev.payment_provider,
        }));
        setHasLogo(Boolean(has_logo));
      } catch {
        /* ignore */
      }
      try {
        const res = await api.get("/fresh-finance/settings/number-series");
        const data = res.data || { years: [], series: {} };
        setSeriesData(data);
        const firstYear = data.years?.[0]?.financial_year_id || "";
        setSeriesYearId(firstYear);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    if (!seriesYearId) return;
    const saved = seriesData.series?.[seriesYearId] || {};
    setSeriesForm({
      bill_prefix: saved.bill?.prefix || "FF-",
      bill_next_number: saved.bill?.next_number || 1,
      receipt_prefix: saved.receipt?.prefix || "RCPT-",
      receipt_next_number: saved.receipt?.next_number || 1,
    });
  }, [seriesData, seriesYearId]);

  const save = async () => {
    try {
      await api.put("/fresh-finance/settings/billing-profile", profile);
      toast.success("Payment details saved — they now appear on bill PDFs");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save payment details");
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 3_000_000) {
      toast.error("Logo must be under 3 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put("/fresh-finance/settings/logo", { logo_b64: reader.result });
        setHasLogo(true);
        toast.success("Logo uploaded — it now prints on bill & receipt letterheads");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Could not upload logo");
      }
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    try {
      await api.put("/fresh-finance/settings/logo", { logo_b64: "" });
      setHasLogo(false);
      toast.success("Logo removed");
    } catch (error) {
      toast.error("Could not remove logo");
    }
  };

  const set = (key) => (e) => setProfile({ ...profile, [key]: e.target.value });

  const saveSeries = async () => {
    if (!seriesYearId) {
      toast.error("Create a financial year before setting document numbers");
      return;
    }
    try {
      await api.put(`/fresh-finance/settings/number-series/${seriesYearId}`, {
        ...seriesForm,
        bill_next_number: Number(seriesForm.bill_next_number || 1),
        receipt_next_number: Number(seriesForm.receipt_next_number || 1),
      });
      toast.success("Bill and receipt numbering series saved");
      const res = await api.get("/fresh-finance/settings/number-series");
      setSeriesData(res.data || { years: [], series: {} });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save numbering series");
    }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="fresh-finance-bill-settings">
      <section className="border border-gray-200 bg-white p-4" data-testid="fresh-finance-logo-section">
        <h3 className="font-display text-sm font-bold">Society logo on bills &amp; receipts</h3>
        <p className="mt-1 text-xs text-gray-600">
          Upload a PNG/JPG logo. It prints in the A4 letterhead of every bill and receipt alongside your
          society name and address.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
              hasLogo ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-gray-200 bg-gray-50 text-gray-600"
            }`}
            data-testid="fresh-finance-logo-status"
          >
            {hasLogo ? "Logo set" : "No logo"}
          </span>
          <label className="cursor-pointer border border-gray-300 px-3 py-1.5 text-sm font-semibold hover:bg-gray-50">
            {hasLogo ? "Replace logo" : "Upload logo"}
            <input
              accept="image/png,image/jpeg"
              className="hidden"
              data-testid="fresh-finance-logo-input"
              disabled={!canManage}
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              type="file"
            />
          </label>
          {hasLogo && canManage && (
            <button
              className="text-xs font-semibold text-rose-700"
              data-testid="fresh-finance-logo-remove"
              onClick={removeLogo}
              type="button"
            >
              Remove
            </button>
          )}
        </div>
      </section>

      <section className="border border-gray-200 bg-white p-4">
        <h3 className="font-display text-sm font-bold">
          Payment details for bill PDFs
        </h3>
        <p className="mt-1 text-xs text-gray-600">
          Society name, address and logo print automatically from your society
          profile. Add payment details once here; each finalized bill freezes its
          own amount and payment reference.
        </p>
        <fieldset
          className="mt-3 grid gap-3 sm:grid-cols-2 disabled:opacity-50"
          disabled={!canManage}
        >
          <Field label="Bank name">
            <Input className="h-8 border-gray-300 text-sm" data-testid="ff-profile-bank-name"
              onChange={set("bank_name")} value={profile.bank_name} />
          </Field>
          <Field label="Account name">
            <Input className="h-8 border-gray-300 text-sm" data-testid="ff-profile-account-name"
              onChange={set("account_name")} value={profile.account_name} />
          </Field>
          <Field label="Account number">
            <Input className="h-8 border-gray-300 text-sm" data-testid="ff-profile-account-number"
              onChange={set("account_number")} value={profile.account_number} />
          </Field>
          <Field label="IFSC">
            <Input className="h-8 border-gray-300 text-sm" data-testid="ff-profile-ifsc"
              onChange={set("ifsc")} value={profile.ifsc} />
          </Field>
          <Field label="Branch">
            <Input className="h-8 border-gray-300 text-sm" data-testid="ff-profile-branch"
              onChange={set("branch")} value={profile.branch} />
          </Field>
          <Field label="UPI ID">
            <Input className="h-8 border-gray-300 text-sm" data-testid="ff-profile-upi"
              onChange={set("upi_id")} value={profile.upi_id} />
          </Field>
          <Field label="Society reply-to email">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="ff-profile-delivery-email"
              onChange={set("delivery_email")}
              type="email"
              value={profile.delivery_email}
            />
          </Field>
          <Field label="Society WhatsApp contact">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="ff-profile-delivery-mobile"
              onChange={set("delivery_mobile")}
              value={profile.delivery_mobile}
            />
          </Field>
          <Field label="QR payee name">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="ff-profile-qr-payee-name"
              onChange={set("payment_payee_name")}
              value={profile.payment_payee_name}
            />
          </Field>
          <Field label="Payment QR provider">
            <select
              className="h-8 border border-gray-300 bg-white px-2 text-sm"
              data-testid="ff-profile-payment-provider"
              onChange={set("payment_provider")}
              value={profile.payment_provider}
            >
              <option value="UPI">UPI exact-amount QR</option>
              <option value="RAZORPAY">Razorpay secure payment QR</option>
              <option value="BANK_ONLY">Bank details only</option>
            </select>
          </Field>
          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 sm:col-span-2">
            <input
              checked={Boolean(profile.payment_qr_enabled)}
              data-testid="ff-profile-payment-qr-enabled"
              onChange={(event) =>
                setProfile({ ...profile, payment_qr_enabled: event.target.checked })
              }
              type="checkbox"
            />
            Print an exact-amount QR on every finalized bill
          </label>
          {profile.payment_provider === "RAZORPAY" && (
            <p
              className="border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:col-span-2"
              data-testid="ff-profile-razorpay-pending"
            >
              A Razorpay QR opens a secure bill checkout and posts a receipt only
              after server-side verification. Your platform administrator must
              configure the Razorpay credentials and secure public application origin.
            </p>
          )}
          <label className="grid gap-1 text-xs font-semibold text-gray-600 sm:col-span-2">
            <span>Payment instructions</span>
            <textarea
              className="min-h-16 border border-gray-300 bg-white p-2 text-sm"
              data-testid="ff-profile-instructions"
              onChange={set("payment_instructions")}
              value={profile.payment_instructions}
            />
          </label>
        </fieldset>
        <div className="mt-3">
          <Button data-testid="ff-profile-save" disabled={!canManage} onClick={save} size="sm" type="button">
            <Save className="h-4 w-4" />
            Save payment details
          </Button>
        </div>
      </section>

      <section className="border border-gray-200 bg-white p-4" data-testid="fresh-finance-series-section">
        <h3 className="font-display text-sm font-bold">Financial year document numbering</h3>
        <p className="mt-1 text-xs text-gray-600">
          Set separate bill and receipt prefixes with their next running number. Issued numbers cannot be reused.
        </p>
        <fieldset className="mt-3 grid gap-3 sm:grid-cols-2 disabled:opacity-50" disabled={!canManage}>
          <Field label="Financial year">
            <select
              className="h-8 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-series-year"
              onChange={(event) => setSeriesYearId(event.target.value)}
              value={seriesYearId}
            >
              <option value="">Select financial year</option>
              {seriesData.years.map((year) => (
                <option key={year.financial_year_id} value={year.financial_year_id}>
                  {year.label}
                </option>
              ))}
            </select>
          </Field>
          <div className="hidden sm:block" />
          <Field label="Bill prefix">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-series-bill-prefix"
              onChange={(event) => setSeriesForm({ ...seriesForm, bill_prefix: event.target.value })}
              value={seriesForm.bill_prefix}
            />
          </Field>
          <Field label="Next bill number">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-series-bill-next"
              min="1"
              onChange={(event) => setSeriesForm({ ...seriesForm, bill_next_number: event.target.value })}
              type="number"
              value={seriesForm.bill_next_number}
            />
          </Field>
          <Field label="Receipt prefix">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-series-receipt-prefix"
              onChange={(event) => setSeriesForm({ ...seriesForm, receipt_prefix: event.target.value })}
              value={seriesForm.receipt_prefix}
            />
          </Field>
          <Field label="Next receipt number">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-series-receipt-next"
              min="1"
              onChange={(event) => setSeriesForm({ ...seriesForm, receipt_next_number: event.target.value })}
              type="number"
              value={seriesForm.receipt_next_number}
            />
          </Field>
        </fieldset>
        <Button
          className="mt-3"
          data-testid="fresh-finance-series-save"
          disabled={!canManage || !seriesYearId}
          onClick={saveSeries}
          size="sm"
          type="button"
        >
          <Save className="h-4 w-4" />
          Save numbering
        </Button>
      </section>
    </div>
  );
}

function OpeningDuesTab({ canManage, charges, units }) {
  const [unitId, setUnitId] = useState("");
  const [asOf, setAsOf] = useState("2026-07-31");
  const [amounts, setAmounts] = useState({});
  const [lateFee, setLateFee] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/fresh-finance/opening-dues");
      setRows(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load opening dues");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const total =
    charges.reduce((sum, c) => sum + Number(amounts[c.charge_id] || 0), 0) +
    Number(lateFee || 0);

  const save = async () => {
    try {
      setBusy(true);
      await api.post("/fresh-finance/opening-dues", {
        unit_id: unitId,
        as_of_date: asOf,
        late_fee_outstanding: Number(lateFee || 0),
        notes,
        lines: charges.map((c) => ({
          charge_id: c.charge_id,
          amount: Number(amounts[c.charge_id] || 0),
        })),
      });
      toast.success("Opening dues posted for the unit");
      setAmounts({});
      setLateFee("");
      setNotes("");
      setUnitId("");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post opening dues");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/fresh-finance/opening-dues/template", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "opening-dues-template.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const runImport = async (commit) => {
    if (!file) {
      toast.error("Choose an .xlsx file first");
      return;
    }
    try {
      setBusy(true);
      const data = new FormData();
      data.append("file", file);
      const res = await api.post(
        `/fresh-finance/opening-dues/import?commit=${commit}`,
        data,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportResult(res.data);
      if (commit) {
        toast.success(`${res.data.counts.posted} unit(s) posted`);
        load();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const reverse = async (bill) => {
    const reason = window.prompt(`Reason for reversing opening dues of ${bill.unit_snapshot?.unit_number}?`);
    if (!reason) return;
    try {
      await api.post(`/fresh-finance/bills/${bill.bill_id}/reverse`, { reason });
      toast.success("Opening dues reversed");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reverse");
    }
  };

  return (
    <div className="mt-4 space-y-5" data-testid="fresh-finance-opening-dues">
      <p className="border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-900">
        Enter each unit&apos;s outstanding as on the day before your first bill
        (e.g. 31 Jul 2026 if billing starts Aug 2026). Maintenance and other
        charges are kept in separate columns so late fee is charged only on
        maintenance arrears.
      </p>
      {charges.length === 0 && (
        <p
          className="border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900"
          data-testid="fresh-finance-opening-dues-charge-hint"
        >
          Add Charge Master items first. The old-data template will then include
          one separate column for every active charge.
        </p>
      )}
      <section
        className="border border-gray-200 bg-white p-4"
        data-testid="fresh-finance-opening-form"
      >
        <h3 className="font-display text-sm font-bold">Enter old data unit-wise</h3>
        <fieldset
          className="mt-3 flex flex-wrap items-end gap-3 disabled:opacity-50"
          disabled={!canManage}
        >
          <Field label="Unit">
            <select
              className="h-8 min-w-40 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-opening-unit"
              onChange={(e) => setUnitId(e.target.value)}
              value={unitId}
            >
              <option value="">Select unit</option>
              {units.map((u) => (
                <option key={u.unit_id} value={u.unit_id}>
                  {u.unit_number}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Outstanding as on">
            <Input
              className="h-8 border-gray-300 text-sm"
              data-testid="fresh-finance-opening-asof"
              onChange={(e) => setAsOf(e.target.value)}
              type="date"
              value={asOf}
            />
          </Field>
          {charges.map((c) => (
            <Field
              key={c.charge_id}
              label={`${c.code}${c.attracts_late_fee === false ? "" : " (maint.)"}`}
            >
              <Input
                className="h-8 w-28 border-gray-300 text-sm"
                data-testid={`fresh-finance-opening-amount-${c.code}`}
                onChange={(e) =>
                  setAmounts({ ...amounts, [c.charge_id]: e.target.value })
                }
                placeholder="0"
                type="number"
                value={amounts[c.charge_id] || ""}
              />
            </Field>
          ))}
          <Field label="Late fee o/s">
            <Input
              className="h-8 w-28 border-gray-300 text-sm"
              data-testid="fresh-finance-opening-late-fee"
              onChange={(e) => setLateFee(e.target.value)}
              placeholder="0"
              type="number"
              value={lateFee}
            />
          </Field>
          <Field label="Notes">
            <Input
              className="h-8 w-40 border-gray-300 text-sm"
              data-testid="fresh-finance-opening-notes"
              onChange={(e) => setNotes(e.target.value)}
              value={notes}
            />
          </Field>
          <div className="text-sm font-semibold" data-testid="fresh-finance-opening-total">
            Total {inr(total)}
          </div>
          <Button
            data-testid="fresh-finance-opening-save"
            disabled={busy || !unitId || total <= 0}
            onClick={save}
            size="sm"
            type="button"
          >
            <Save className="h-4 w-4" />
            Post opening dues
          </Button>
        </fieldset>
      </section>

      <section
        className="border border-gray-200 bg-white p-4"
        data-testid="fresh-finance-opening-import"
      >
        <h3 className="font-display text-sm font-bold">Import old data from Excel</h3>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            data-testid="fresh-finance-opening-template"
            onClick={downloadTemplate}
            size="sm"
            type="button"
            variant="outline"
          >
            <Download className="h-4 w-4" />
            Download template
          </Button>
          <input
            accept=".xlsx"
            data-testid="fresh-finance-opening-file"
            disabled={!canManage}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            type="file"
            className="text-sm"
          />
          <Button
            data-testid="fresh-finance-opening-import-preview"
            disabled={!canManage || busy || !file}
            onClick={() => runImport(false)}
            size="sm"
            type="button"
            variant="outline"
          >
            Validate
          </Button>
          <Button
            data-testid="fresh-finance-opening-import-commit"
            disabled={!canManage || busy || !file}
            onClick={() => runImport(true)}
            size="sm"
            type="button"
          >
            <Upload className="h-4 w-4" />
            Import &amp; post
          </Button>
        </div>
        {importResult && (
          <div className="mt-3 text-xs" data-testid="fresh-finance-opening-import-result">
            <p className="font-semibold">
              Valid {importResult.counts.valid} · Errors {importResult.counts.errors} ·
              Posted {importResult.counts.posted}
            </p>
            <ul className="mt-1 max-h-40 overflow-auto">
              {importResult.rows
                .filter((r) => r.errors.length)
                .map((r) => (
                  <li key={r.row} className="text-rose-700">
                    Row {r.row} ({r.unit_number}): {r.errors.join("; ")}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </section>

      <div className="overflow-x-auto border border-gray-200" data-testid="fresh-finance-opening-table">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2">Member</th>
              <th className="px-3 py-2">As on</th>
              <th className="px-3 py-2 text-right">Maintenance</th>
              <th className="px-3 py-2 text-right">Other</th>
              <th className="px-3 py-2 text-right">Late fee</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {rows.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-gray-500" colSpan={9}>
                  No opening dues posted yet.
                </td>
              </tr>
            )}
            {rows.map((bill) => (
              <tr key={bill.bill_id} data-testid={`fresh-finance-opening-row-${bill.unit_snapshot?.unit_number}`}>
                <td className="px-3 py-2 font-medium">{bill.unit_snapshot?.unit_number}</td>
                <td className="px-3 py-2">{bill.member_snapshot?.name || "—"}</td>
                <td className="px-3 py-2">{bill.period_snapshot?.end_date}</td>
                <td className="px-3 py-2 text-right tabular-nums">{inr(bill.maintenance_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{inr(bill.other_charge_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{inr(bill.interest_amount)}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{inr(bill.grand_total)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[bill.status]}`}>
                    {bill.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {bill.status === "FINALIZED" && canManage && (
                    <button
                      className="text-xs font-semibold text-rose-700"
                      data-testid={`fresh-finance-opening-reverse-${bill.unit_snapshot?.unit_number}`}
                      onClick={() => reverse(bill)}
                      type="button"
                    >
                      Reverse
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ children, label }) {
  return (
    <label className="grid gap-1 text-xs font-semibold text-gray-600">
      <span>{label}</span>
      {children}
    </label>
  );
}
